/**
 * Kleine Hilfen der Arbeitsschritt-Liste: Gruppierung nach Abschnitt, Ziele
 * fürs Verschieben, Auswahl beim Zusammenführen, Texte. Reine Funktionen,
 * damit sie ohne Browser testbar sind — die eigentliche Ordnungs- und
 * Zusammenführlogik liegt in `abschnittOrdnung.ts` und
 * `schritteZusammenfuehren.ts`.
 */
import { Arbeitsschritt, HAUPTABSCHNITTE } from './types';
import { OHNE_ABSCHNITT, abschnittVon } from './abschnittOrdnung';
import type { ZusammenfuehrStufe } from './schritteZusammenfuehren';
import { clampDatum } from './zeitraum';

// Editierbare Felder eines Schritts (ohne IDs/Sortierung) — das Formular des Bearbeiten-Dialogs.
export type SchrittForm = Omit<Arbeitsschritt, 'id' | 'sige_projekt_id' | 'sortierung'>;

/** Ein Abschnitt: `schluessel` wie `abschnittVon` ('' = ohne Abschnitt), `name` zur Anzeige. */
export interface AbschnittZiel {
  schluessel: string;
  name: string;
}

export interface SchrittGruppe extends AbschnittZiel {
  schritte: Arbeitsschritt[];
}

/** Eingestellte Reihenfolge der Abschnitte, sonst die Standardliste. */
const reihenfolgeAus = (hauptabschnitte: readonly string[]): string[] =>
  (hauptabschnitte.length ? hauptabschnitte : HAUPTABSCHNITTE).map(h => h.trim()).filter(Boolean);

/**
 * Schritte nach Abschnitt gruppiert, Gruppen in der eingestellten
 * Reihenfolge; Unbekannte und „Ohne Abschnitt“ dahinter in der Reihenfolge
 * ihres ersten Auftretens. Innerhalb der Gruppe bleibt die Listenreihenfolge.
 */
export const gruppiereSchritte = (schritte: Arbeitsschritt[], hauptabschnitte: readonly string[]): SchrittGruppe[] => {
  const reihenfolge = reihenfolgeAus(hauptabschnitte);
  const map = new Map<string, Arbeitsschritt[]>();
  schritte.forEach(s => {
    const k = abschnittVon(s);
    const liste = map.get(k);
    if (liste) liste.push(s);
    else map.set(k, [s]);
  });
  const rang = (k: string) => {
    const i = k ? reihenfolge.indexOf(k) : -1;
    return i === -1 ? 999 : i;
  };
  return Array.from(map.entries())
    .sort((a, b) => rang(a[0]) - rang(b[0]))
    .map(([schluessel, liste]) => ({ schluessel, name: schluessel || OHNE_ABSCHNITT, schritte: liste }));
};

/**
 * Ziele für „In anderen Abschnitt verschieben“ und „Zusammenführen mit…“:
 * die eingestellten Abschnitte, dann die, die nur in Schritten vorkommen,
 * zuletzt „Ohne Abschnitt“ — je Schlüssel einmal. Den eigenen Abschnitt
 * blendet der Aufrufer aus.
 */
export const abschnittsZiele = (
  hauptabschnitte: readonly string[],
  schritte: Pick<Arbeitsschritt, 'hauptabschnitt'>[],
): AbschnittZiel[] => {
  const gesehen = new Set<string>();
  const ziele: AbschnittZiel[] = [];
  for (const k of [...reihenfolgeAus(hauptabschnitte), ...schritte.map(s => abschnittVon(s))]) {
    if (!k || gesehen.has(k)) continue;
    gesehen.add(k);
    ziele.push({ schluessel: k, name: k });
  }
  ziele.push({ schluessel: '', name: OHNE_ABSCHNITT });
  return ziele;
};

/** Je Schritt-Id ihre Gruppe gleich bezeichneter Schritte (nur Gruppen ab 2). */
export const dublettenJeSchritt = (gruppen: string[][]): Map<string, string[]> => {
  const m = new Map<string, string[]>();
  gruppen.forEach(g => {
    if (g.length >= 2) g.forEach(id => m.set(id, g));
  });
  return m;
};

/** Title der Dubletten-Marke: welche Zeilen gleich heißen (mit Abschnitt). */
export const dublettenTitel = (
  gruppe: Pick<Arbeitsschritt, 'bezeichnung' | 'hauptabschnitt'>[],
  klickbar: boolean,
): string => {
  const liste = gruppe.map(s => `„${s.bezeichnung}“ (${abschnittVon(s) || OHNE_ABSCHNITT})`).join(', ');
  return `Gleich bezeichnet: ${liste}${klickbar ? '. Zum Zusammenführen anklicken' : ''}`;
};

/** Schritte ohne vollständigen Zeitraum (von UND bis) — sie bekommen im Plan keinen Balken. */
export const ohneZeitraumAnzahl = (schritte: Pick<Arbeitsschritt, 'zeitraum_von' | 'zeitraum_bis'>[]): number =>
  schritte.filter(s => !(s.zeitraum_von || '').trim() || !(s.zeitraum_bis || '').trim()).length;

/** Häkchen einer Zeile umschalten (neue Menge). */
export const umschalten = (auswahl: ReadonlySet<string>, id: string): Set<string> => {
  const neu = new Set(auswahl);
  if (neu.has(id)) neu.delete(id);
  else neu.add(id);
  return neu;
};

/** Zielzeile: die gewünschte, solange sie gewählt ist — sonst die erste der Liste. */
export const zielDerAuswahl = (gewaehlt: string[], wunsch: string | null): string | null =>
  wunsch && gewaehlt.includes(wunsch) ? wunsch : (gewaehlt[0] ?? null);

/** Hinweis im Zusammenführen-Dialog: was danach gelöscht wird. */
export const zusammenfuehrHinweis = (quellen: Pick<Arbeitsschritt, 'bezeichnung'>[]): string => {
  const namen = quellen.map(q => `„${q.bezeichnung}“`).join(', ');
  return `${namen} ${quellen.length === 1 ? 'wird' : 'werden'} danach gelöscht; Gefährdungen wandern an die Zielzeile.`;
};

/** Rückfrage beim Abschnitt-Zusammenführen. */
export const abschnittZusammenfuehrFrage = (anzahl: number, von: string, nach: string): string =>
  `${anzahl} ${anzahl === 1 ? 'Arbeitsschritt' : 'Arbeitsschritte'} von „${von}“ nach „${nach}“ verschieben?`;

/** Fehler beim Schreiben der Ordnung, mit Bezeichnung statt Id. */
export const ordnungsFehlerMeldung = (
  fehler: { id: string; meldung: string }[],
  schritte: Pick<Arbeitsschritt, 'id' | 'bezeichnung'>[],
): string | null => {
  if (!fehler.length) return null;
  const name = new Map(schritte.map(s => [s.id, s.bezeichnung]));
  const einzeln = fehler.map(f => `„${name.get(f.id) ?? f.id}“ (${f.meldung})`).join('; ');
  return fehler.length === 1
    ? `Verschieben: 1 Arbeitsschritt konnte nicht gespeichert werden: ${einzeln}`
    : `Verschieben: ${fehler.length} Arbeitsschritte konnten nicht gespeichert werden: ${einzeln}`;
};

/**
 * Bleibt der Zusammenführen-Dialog nach einem Fehler offen? Nur solange die
 * Zielzeile noch nicht geschrieben ist — danach ist der Teilstand in der
 * Datenbank, der Dialog schließt und die Liste übernimmt ihn. 'konflikt'
 * schließt ihn ebenfalls (die Vereinigung ist veraltet, ein erneuter Klick
 * scheiterte gleich wieder), die Auswahl bleibt aber für „Weiter“ stehen.
 */
export const dialogBleibtOffen = (stufe: ZusammenfuehrStufe): boolean => stufe === 'pruefung' || stufe === 'ziel';

/** Ob nach dem Fehler noch nichts geschrieben ist. */
const nichtsGeschrieben = (stufe: ZusammenfuehrStufe): boolean => dialogBleibtOffen(stufe) || stufe === 'konflikt';

export const zusammenfuehrFehlerText = (stufe: ZusammenfuehrStufe, meldung: string): string =>
  nichtsGeschrieben(stufe) ? `Zusammenführen fehlgeschlagen: ${meldung}` : `Zusammenführen unvollständig: ${meldung}`;

/** Formularstand aus einem Schritt oder aus vereinigten Feldern (Bearbeiten, Zusammenführen). */
export const formAus = (s: SchrittForm): SchrittForm => ({
  bezeichnung: s.bezeichnung,
  gewerk: s.gewerk || '',
  hauptabschnitt: s.hauptabschnitt || '',
  zeitraum_von: s.zeitraum_von || '',
  zeitraum_bis: s.zeitraum_bis || '',
  schutzmassnahmen: s.schutzmassnahmen || [],
  vorschriften: s.vorschriften || [],
  mitgeltende_dokumente: s.mitgeltende_dokumente || [],
  hinweis_code: s.hinweis_code || '',
  hinweis_codes: s.hinweis_codes?.length
    ? s.hinweis_codes
    : (s.hinweis_code || '').split(';').map(t => t.trim()).filter(t => t && t.toLowerCase() !== 'n.v.'),
  kritisch: !!s.kritisch,
  koordinierungsbedarf: s.koordinierungsbedarf || 'alle Gewerke, Dritte',
  zustaendig_erstellung: s.zustaendig_erstellung || '',
  zustaendig_kontrolle: s.zustaendig_kontrolle || '',
  zustaendig_instandhaltung: s.zustaendig_instandhaltung || '',
});

/**
 * Zeitraum zum Speichern: nie vor Projekt-Start / nach Projekt-Ende. Ohne
 * Zeitachse sind die Datumsfelder gesperrt — dann bleibt der Wert, wie er
 * ist, statt still geklemmt zu werden.
 */
export const klemmeZeitraum = (f: SchrittForm, ohneZeitachse: boolean, projektVon?: string, projektBis?: string): SchrittForm =>
  ohneZeitachse
    ? f
    : {
      ...f,
      zeitraum_von: clampDatum(f.zeitraum_von, projektVon, projektBis),
      zeitraum_bis: clampDatum(f.zeitraum_bis, projektVon, projektBis),
    };
